import type { IUserInfo } from "@/account/account-models";
import { EUserType } from "@/account/account-models";
import { IHeaderModalActions } from "@/common/header-modal/header-modal-actions";
import { IFeedActions } from "@/feed/feed-actions";
import type { IFeedInfo } from "@/feed/feed-models";
import type { IDictionary } from "@/models";
import { removeDuplicates } from "@/utils/array";
import { createReducer } from "@reduxjs/toolkit";
import { IDiscoverActions } from "./discover-actions";
import type { IDiscoverFilter, IGetProfilesConfig } from "./discover-helpers";
import { getDefaultDiscoverFiltersState } from "./discover-helpers";
import type { IDiscoverAlerts } from "./discover-models";
import { EDiscoverPunishmentAlertType } from "./discover-models";

// Extended interface for discover cards with profile feed data
export interface IDiscoverCardWithFeed extends IUserInfo {
  __isImpressionTracked?: boolean;
  profileFeed?: IFeedInfo[];
  nextProfileFeedUrl?: string;
}

export interface IDiscoverModel {
  isInitialRequest: boolean;
  filtersSet: IDictionary<IDiscoverFilter>;
  profiles: (IUserInfo & { __isImpressionTracked?: boolean })[];
  nextProfilesUrl: string | null;
  isProfilesStale: boolean; // filters changed on the cards screen, list must reload
  rank: number | null;
  alerts: IDiscoverAlerts;
  discoverCards: IDiscoverCardWithFeed[];
  discoverCardsHistory: IDiscoverCardWithFeed[];
}

const discoverStoreInitialState: IDiscoverModel = {
  isInitialRequest: true,
  filtersSet: getDefaultDiscoverFiltersState(),
  profiles: [],
  nextProfilesUrl: null,
  isProfilesStale: false,
  rank: null,
  alerts: {
    [EDiscoverPunishmentAlertType.Punishment]: false,
    [EDiscoverPunishmentAlertType.FinalPunishment]: false,
  },
  discoverCards: [],
  discoverCardsHistory: [],
};

/** Reducer */
export const discoverReducer = createReducer(
  discoverStoreInitialState,
  (builder) => {
    builder
      .addCase(
        IDiscoverActions.initGetProfiles,
        (state, action): IDiscoverModel => {
          const { isInitialRequest, isForceLoader } = action.payload
            .config as IGetProfilesConfig;
          return {
            ...state,
            isInitialRequest,
            isProfilesStale: false,
            // listScrollY: isInitialRequest ? 0 : state.listScrollY,
            // profiles: isInitialRequest ? [] : state.profiles,
            profiles: isForceLoader ? [] : state.profiles,
            discoverCards: isInitialRequest ? [] : state.discoverCards,
            discoverCardsHistory: isInitialRequest
              ? []
              : state.discoverCardsHistory,
          };
        }
      )
      .addCase(
        IDiscoverActions.successGetProfiles,
        (state, action): IDiscoverModel => {
          const { config, res } = action.payload;
          let profiles: typeof state.profiles;
          if (config.isInitialRequest) {
            profiles = res.data;
          } else {
            profiles = [...state.profiles, ...res.data];
          }
          profiles = removeDuplicates(profiles, "username");
          return {
            ...state,
            nextProfilesUrl: res.next || null,
            profiles,
            alerts: {
              [EDiscoverPunishmentAlertType.Punishment]: res.punishment_alert,
              [EDiscoverPunishmentAlertType.FinalPunishment]:
                res.final_punishment_alert,
            },
            rank: res.rank,
            filtersSet: config.filtersSet,
          };
        }
      )
      .addCase(
        IDiscoverActions.setDiscoverAlertConfirmed,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            alerts: {
              ...state.alerts,
              [action.payload.type]: false,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.setDiscoverProfileImpressionTracked,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            profiles: state.profiles.map((profile) => {
              if (profile.username === action.payload) {
                return {
                  ...profile,
                  __isImpressionTracked: true,
                };
              }
              return profile;
            }),
          };
        }
      )
      .addCase(
        IDiscoverActions.setDiscoverCardsFilters,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            filtersSet: action.payload,
            discoverCards: [],
            discoverCardsHistory: [],
            isProfilesStale: true,
          };
        }
      )
      .addCase(
        IDiscoverActions.successGetDiscoverCards,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            discoverCards: [
              ...state.discoverCards,
              ...(action.payload.data || []).filter(
                (c: IUserInfo) =>
                  c.type === EUserType.Featured &&
                  !c.is_followee &&
                  !state.discoverCardsHistory.find(
                    (historyCard) => historyCard._id === c._id
                  ) &&
                  !state.discoverCards.find((card) => card._id === c._id)
              ),
            ],
          };
        }
      )
      .addCase(
        IDiscoverActions.advanceDiscoverCard,
        (state): IDiscoverModel => {
          return {
            ...state,
            discoverCards: state.discoverCards.filter((c, i) => i !== 0),
            discoverCardsHistory: [
              ...state.discoverCardsHistory,
              state.discoverCards[0],
            ],
          };
        }
      )
      .addCase(
        IDiscoverActions.setDiscoverCardImpressionTracked,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            discoverCards: state.discoverCards.map((card) => {
              if (card.username === action.payload) {
                return {
                  ...card,
                  __isImpressionTracked: true,
                };
              }
              return card;
            }),
          };
        }
      )
      .addCase(
        IDiscoverActions.successGetCardProfileFeed,
        (state, action): IDiscoverModel => {
          const { userId, profileFeed, nextProfileFeedUrl } = action.payload;
          return {
            ...state,
            discoverCards: state.discoverCards.map((card) => {
              if (card._id === userId) {
                return {
                  ...card,
                  profileFeed,
                  nextProfileFeedUrl,
                };
              }
              return card;
            }),
          };
        }
      )
      // A post edited or removed from a profile's viewer must change in that
      // profile's own list too (it is a separate copy from the feed's).
      .addCase(
        IFeedActions.feedItemPatched,
        (state, action): IDiscoverModel => {
          const { data } = action.payload;
          return {
            ...state,
            discoverCards: state.discoverCards.map((card) =>
              card.profileFeed
                ? {
                    ...card,
                    profileFeed: card.profileFeed.map((item) =>
                      item._id === data._id ? data : item
                    ),
                  }
                : card
            ),
          };
        }
      )
      .addCase(
        IHeaderModalActions.feedItemDeleted,
        (state, action): IDiscoverModel => ({
          ...state,
          discoverCards: state.discoverCards.map((card) =>
            card.profileFeed
              ? {
                  ...card,
                  profileFeed: card.profileFeed.filter(
                    (item) => item._id !== action.payload.id
                  ),
                }
              : card
          ),
        })
      )
      .addCase(
        IDiscoverActions.successGetCardProfileFeedNext,
        (state, action): IDiscoverModel => {
          const { userId, profileFeed, nextProfileFeedUrl } = action.payload;
          return {
            ...state,
            discoverCards: state.discoverCards.map((card) => {
              if (card._id === userId) {
                const existingFeed = card.profileFeed || [];
                const newFeed = [...existingFeed, ...profileFeed];
                return {
                  ...card,
                  profileFeed: newFeed,
                  nextProfileFeedUrl,
                };
              }
              return card;
            }),
          };
        }
      );
  }
);
